import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Info } from 'lucide-react';
import { apiCountries, apiPublicHolidays, apiRegions } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import DatePicker from '../components/DatePicker.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';

/**
 * Sprint 5 — Vue admin des jours fériés.
 *
 * Filtres pills : année + pays. CRUD modal. L'import depuis Nager est
 * automatique (batch `public.holidays.import`, piloté dans synobatch) ; le
 * bandeau d'info le rappelle.
 */

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

function toDateInput(iso) {
  if (!iso) return '';
  return new Date(iso).toISOString().slice(0, 10);
}

const CURRENT_YEAR = new Date().getUTCFullYear();
const YEAR_OPTIONS = [
  CURRENT_YEAR - 1,
  CURRENT_YEAR,
  CURRENT_YEAR + 1,
  CURRENT_YEAR + 2,
];

const EMPTY = {
  date: '',
  name: '',
  nagerType: '',
  countryCode: '',
  regionCode: '',
};

export default function PublicHolidaysView() {
  const [items, setItems] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');

  const [filterYear, setFilterYear] = useState(CURRENT_YEAR);
  const [filterCountry, setFilterCountry] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Régions du pays sélectionné dans la modale (pour le picker).
  const [formRegions, setFormRegions] = useState([]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiPublicHolidays.list({
        countryCode: filterCountry || undefined,
        year: filterYear,
        archived: archivedFilter,
      });
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter, filterCountry, filterYear]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    apiCountries
      .list('active')
      .then(({ items: data }) => setCountries(data))
      .catch(() => {});
  }, []);

  // Recharge la liste des régions du pays sélectionné quand `form.countryCode` change.
  useEffect(() => {
    if (!form.countryCode) {
      setFormRegions([]);
      return;
    }
    apiRegions
      .list(form.countryCode, 'active')
      .then(({ items: data }) => setFormRegions(data))
      .catch(() => setFormRegions([]));
  }, [form.countryCode]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY,
      countryCode: filterCountry || countries[0]?.code || '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (h) => {
    setEditing(h);
    setForm({
      date: toDateInput(h.date),
      name: h.name,
      nagerType: h.nagerType ?? '',
      countryCode: h.countryCode,
      regionCode: h.regionCode ?? '',
    });
    setFormError(null);
    setModalOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      const payload = {
        date: form.date,
        name: form.name.trim(),
        nagerType: form.nagerType.trim() || null,
        countryCode: form.countryCode,
        regionCode: form.regionCode || null,
      };
      if (editing) {
        await apiPublicHolidays.update(editing.id, payload);
      } else {
        await apiPublicHolidays.create(payload);
      }
      setModalOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (h) => {
    if (!window.confirm(`Archiver le férié "${h.name}" du ${toDateInput(h.date)} ?`)) return;
    await apiPublicHolidays.archive(h.id);
    await reload();
  };

  const onRestore = async (h) => {
    await apiPublicHolidays.restore(h.id);
    await reload();
  };

  const sortedItems = useMemo(
    () =>
      [...items].sort((a, b) => {
        const da = a.date ?? '';
        const db = b.date ?? '';
        if (da !== db) return da.localeCompare(db);
        return (a.countryCode ?? '').localeCompare(b.countryCode ?? '');
      }),
    [items],
  );

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1300, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Jours fériés</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />

        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Année :</span>
          {YEAR_OPTIONS.map((y) => (
            <button
              key={y}
              type="button"
              onClick={() => setFilterYear(y)}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                border: '1px solid var(--glass-border)',
                background: filterYear === y ? 'var(--primary)' : 'var(--glass)',
                color: filterYear === y ? '#fff' : 'var(--text)',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {y}
            </button>
          ))}
        </div>

        <div style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Pays :</span>
          <button
            type="button"
            onClick={() => setFilterCountry('')}
            style={{
              padding: '4px 12px',
              borderRadius: 999,
              border: '1px solid var(--glass-border)',
              background: filterCountry === '' ? 'var(--primary)' : 'var(--glass)',
              color: filterCountry === '' ? '#fff' : 'var(--text)',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Tous
          </button>
          {countries.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => setFilterCountry(c.code)}
              style={{
                padding: '4px 12px',
                borderRadius: 999,
                border: '1px solid var(--glass-border)',
                background: filterCountry === c.code ? 'var(--primary)' : 'var(--glass)',
                color: filterCountry === c.code ? '#fff' : 'var(--text)',
                fontSize: 12,
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              {c.code}
            </button>
          ))}
        </div>

        <button type="button" onClick={openCreate} style={{ ...primaryBtn, marginLeft: 'auto' }}>
          <Plus size={14} /> Nouveau férié
        </button>
      </div>

      <div
        style={{
          background: 'var(--glass)',
          border: '1px dashed var(--glass-border)',
          borderRadius: 'var(--radius-sm)',
          padding: '8px 14px',
          marginBottom: 12,
          fontSize: 12,
          color: 'var(--text-muted)',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <Info size={14} />
        <span>
          Import automatique depuis l'API Nager le 1er de chaque mois (année en cours et
          suivante) : seuls les fériés absents sont créés, vos modifications et archivages
          sont conservés. Relance possible depuis la console synobatch.
        </span>
      </div>

      {error ? <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div> : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'auto',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 110 }}>Date</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Nom</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 90 }}>Pays</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 120 }}>Région</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Type Nager</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 100 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td></tr>
            ) : sortedItems.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)' }}>Aucun jour férié.</td></tr>
            ) : (
              sortedItems.map((h) => (
                <tr key={h.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                    {toDateInput(h.date)}
                    {h.archivedAt ? (
                      <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>(archivé)</span>
                    ) : null}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text)' }}>{h.name}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {h.country?.name ?? h.countryCode}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>
                    {h.region?.name ?? '— national'}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{h.nagerType ?? '—'}</td>
                  <td style={{ padding: '10px 12px' }}>
                    <EntityActions
                      archived={Boolean(h.archivedAt)}
                      onEdit={() => openEdit(h)}
                      onArchive={() => onArchive(h)}
                      onRestore={() => onRestore(h)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Modifier le jour férié' : 'Nouveau jour férié'}
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="holiday-form"
              disabled={
                submitting ||
                !form.date ||
                !form.name.trim() ||
                !form.countryCode
              }
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              Enregistrer
            </button>
          </>
        }
      >
        <form id="holiday-form" onSubmit={onSubmit} style={{ display: 'grid', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                Date
              </label>
              <DatePicker
                value={form.date}
                onChange={(v) => setForm({ ...form, date: v })}
                required
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
                Pays
              </label>
              <select
                value={form.countryCode}
                onChange={(e) =>
                  setForm({ ...form, countryCode: e.target.value, regionCode: '' })
                }
                required
                style={inputStyle}
              >
                <option value="">— Sélectionner —</option>
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Région (vide = férié national)
            </label>
            <select
              value={form.regionCode}
              onChange={(e) => setForm({ ...form, regionCode: e.target.value })}
              disabled={!form.countryCode || formRegions.length === 0}
              style={inputStyle}
            >
              <option value="">— National (toutes régions) —</option>
              {formRegions.map((r) => (
                <option key={r.code} value={r.code}>
                  {r.code} — {r.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Nom
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
              maxLength={128}
              placeholder="Jour de l'An"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Type Nager (optionnel — pour traçabilité)
            </label>
            <input
              type="text"
              value={form.nagerType}
              onChange={(e) => setForm({ ...form, nagerType: e.target.value })}
              maxLength={64}
              placeholder="New Year's Day"
              style={inputStyle}
            />
          </div>
          {formError ? <div style={{ color: 'var(--danger)', fontSize: 13 }}>{formError}</div> : null}
        </form>
      </Modal>

    </div>
  );
}
